/**
 * Plan projection — display-list primitive vocabulary.
 *
 * A plan projection emits these pure, canvas-free primitives in pane-local
 * pixels. The drawer is the only side effect: it translates to the pane origin,
 * resolves a colour ref against the (already monochrome-aware) palette, and
 * executes each primitive. See CONTEXT.md (Plan projection · Drawer · Colour ref).
 */

/** Semantic colour name; its value is the key into the resolved palette. */
export enum EColorRef {
    Paper = 'paper',
    Paper2 = 'paper2',
    Paper3 = 'paper3',
    Rule = 'rule',
    Ink = 'ink',
    Ink2 = 'ink2',
    Ink3 = 'ink3',
    Ink4 = 'ink4',
    Ink5 = 'ink5',
    Warn = 'warn',
    Err = 'err',
    Step = 'step',
    StepStroke = 'stepStroke',
    Winder = 'winder',
    Landing = 'landing',
    Opening = 'opening',
    StairWidth = 'stairWidth',
    Handrail = 'handrail',
    Post = 'post',
    Baluster = 'baluster',
    Axis = 'axis',
}

/** What a text is to the sheet, when it is more than a label on the drawing. */
export enum ETextRole {
    /** A note about the whole view (pitch, Blondel): the sheet sets it above the drawing. */
    Note = 'note',
    /** A floor-level label: the sheet sets it beside the drawing, at its level. */
    Level = 'level',
}

export enum EPrimitiveKind {
    Poly = 'poly',
    Text = 'text',
    Dim = 'dim',
    Arc = 'arc',
    Hatch = 'hatch',
}

export enum ELineCap {
    Butt = 'butt',
    Round = 'round',
}

export enum ELineJoin {
    Miter = 'miter',
    Round = 'round',
}

export enum ETextAlign {
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

export enum ETextBaseline {
    Top = 'top',
    Middle = 'middle',
    Bottom = 'bottom',
}

export enum EDimOrientation {
    Horizontal = 'h',
    Vertical = 'v',
}

/** A resolved colour palette: every colour ref maps to a concrete CSS value. */
export type IPlanPalette = Record<EColorRef, string>;

export type IPoint = readonly [number, number];

/** Label-box bounds in pane-local pixels: [x1, y1, x2, y2]. */
export type IBox = readonly [number, number, number, number];

export interface IFont {
    weight: number;
    size: number;
    family: string;
}

/**
 * A polyline or polygon. `closed` polygons may carry a `fill`; any may `stroke`.
 * Optional `fillAlpha`/`strokeAlpha` (0..1) tint the fill/stroke; `dash` makes a
 * dashed stroke; `cap`/`join` set the line cap/join (used by the handrail).
 *
 * `bulges` turns segments into circular arcs, one value per vertex for the segment that
 * starts there (the last one is the closing segment of a closed polygon): `tan(sweep / 4)`,
 * measured in the pane's own pixel frame, positive when the sweep turns from +x toward +y,
 * zero for a straight segment. It is the DXF vertex bulge before the sheet-wide Y flip.
 */
export interface IPolyPrimitive {
    kind: EPrimitiveKind.Poly;
    points: readonly IPoint[];
    closed: boolean;
    bulges?: readonly number[];
    fill?: EColorRef;
    stroke?: EColorRef;
    width?: number;
    fillAlpha?: number;
    strokeAlpha?: number;
    dash?: readonly number[];
    cap?: ELineCap;
    join?: ELineJoin;
}

/**
 * A circular arc (or full circle when `startAngle`/`endAngle` span 2π). May fill
 * and/or stroke; `dash` makes a dashed stroke. Used by the circular opening.
 */
export interface IArcPrimitive {
    kind: EPrimitiveKind.Arc;
    center: IPoint;
    radius: number;
    startAngle: number;
    endAngle: number;
    fill?: EColorRef;
    stroke?: EColorRef;
    width?: number;
    fillAlpha?: number;
    strokeAlpha?: number;
    dash?: readonly number[];
    /** Sweep the arc counter-clockwise (matches the 6th arg of CanvasRenderingContext2D.arc). */
    anticlockwise?: boolean;
}

/**
 * A diagonal hatch clipped to a rectangle — parallel lines spaced `step` px,
 * running top-left to bottom-right. Used to texture the rectangular opening.
 */
export interface IHatchPrimitive {
    kind: EPrimitiveKind.Hatch;
    rect: IPaneRect;
    step: number;
    color: EColorRef;
    alpha?: number;
    width?: number;
}

export interface ITextPrimitive {
    kind: EPrimitiveKind.Text;
    at: IPoint;
    text: string;
    align: ETextAlign;
    baseline: ETextBaseline;
    color: EColorRef;
    font: IFont;
    /** Optional rotation in radians applied around `at` (e.g. a hypotenuse label). */
    rotation?: number;
    /**
     * A label the drawing can do without (a step number): it stays where it stands or, when
     * another label is there, is left out — it is never moved off what it marks.
     */
    optional?: boolean;
    /** A note or a level label the sheet moves out of the drawing; none for a label on it. */
    role?: ETextRole;
}

/**
 * A dimension line with end ticks and a centred label. Horizontal labels sit
 * above the line; vertical labels are rotated. `labelBox` is the label's
 * resolved bounds (via the text measurer), used to assert de-overlap.
 */
export interface IDimPrimitive {
    kind: EPrimitiveKind.Dim;
    orientation: EDimOrientation;
    from: IPoint;
    to: IPoint;
    tick: number;
    label: string;
    labelBox: IBox;
    /**
     * How far the label is moved along the dimension from its middle, pixels (along X for a
     * horizontal dimension, along screen Y for a vertical one). A label too long for its span
     * stands outside it, past one end, and the dimension line is carried on under it.
     */
    labelShift?: number;
    color: EColorRef;
    font: IFont;
}

export type IPlanPrimitive = IPolyPrimitive | ITextPrimitive | IDimPrimitive | IArcPrimitive | IHatchPrimitive;

export interface IPaneRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * What one projection returns: its display list plus the fit it computed.
 * `pxPerMm` is the fit scale, `origin` the pane-local pixel point of the
 * drawing's millimetre origin, so `(px − origin) / pxPerMm` gives millimetres
 * and the consumer flips Y once for the whole sheet.
 *
 * A pane whose second axis runs down the screen (the top view's Z, the steps
 * schema's rise, the flat templates) reports millimetres growing downward — the
 * sheet-wide flip is what turns them the right way up.
 *
 * A pane with no millimetre scale at all — a dial, or two flights drawn at two
 * different scales — reports `pxPerMm: null`. It is not the same as a scale of
 * one: a consumer that writes millimetres out of this pane, a drawing file
 * among them, would otherwise take pixels for millimetres and say nothing.
 */
export interface IProjectionOutput {
    primitives: IPlanPrimitive[];
    pxPerMm: number | null;
    origin: { x: number; y: number };
}

/**
 * A fit the sheet imposes on a view of the projection group instead of the view fitting
 * itself into its pane: one scale shared by the group, and the pane-local pixel point the
 * view's millimetre origin goes to. Views that share an axis then line up on the sheet.
 */
export interface IViewFit {
    pxPerMm: number;
    origin: { x: number; y: number };
}

/** Nothing to draw — a projection whose input is degenerate (no steps, zero rise, pane too small). */
export function emptyProjection(): IProjectionOutput {
    return { primitives: [], pxPerMm: null, origin: { x: 0, y: 0 } };
}

export const FONT_MONO_FAMILY: string = "'JetBrains Mono', ui-monospace, monospace";

export const DIM_FONT: IFont = { weight: 400, size: 10, family: FONT_MONO_FAMILY };
